import { TOAST_TIMEOUT } from 'src/config/Toast';
import store from 'src/store';
import { type RegionForm, type RegionFormTableRow } from 'src/store/adminConfigPageSlice/regionTasksPageSlice/types';
import { createToast } from 'src/store/toastsSlice';
import type { ServiceOptions } from 'src/types';
import apiClient from 'src/utils/apiClient';

import type {
  MeasureUnit,
  RegionTask,
  RegionTasksAndGroupsDTO,
  Task,
  TaskGroupDTO,
  TaskGroupDTOPayload,
} from './types';

// **** Functions **** //

/**
 * Fetch units of measure
 */
async function fetchMeasureUnits(): Promise<MeasureUnit[]> {
  const { data } = await apiClient.get('/measureUnits');
  return data;
}

/**
 * Fetch regions
 */
async function fetchRegions(): Promise<RegionForm[]> {
  const { data } = await apiClient.get('/regions');
  return data;
}

/**
 * Fetch all tasks
 */
async function fetchTasks(): Promise<Task[]> {
  const { data } = await apiClient.get('/tasks');
  return data;
}

/**
 * Create or update task (create if id not provided)
 */
async function createOrUpdateTask(data: Task): Promise<Task> {
  const res = await apiClient.post('/tasks', data);
  if (res) {
    store.dispatch(
      createToast({
        type: 'success',
        message: data.id ? 'Updated task successfully' : 'Created task successfully',
        timeout: TOAST_TIMEOUT,
      }),
    );
  }
  return res.data;
}

/**
 * Get RegionTasks by regionId.
 */
async function fetchRegionTasksByRegionId(regionId: number, options?: ServiceOptions): Promise<RegionTask[]> {
  const { data } = await apiClient.get(`/regionTasks/${regionId}`, { signal: options?.signal });
  return data;
}

/**
 * Update a list of RegionTasks
 */
async function updateRegionTasks(data: RegionTask[]): Promise<RegionTask[]> {
  const res = await apiClient.post('/regionTasks', data);
  return res.data;
}

/**
 * Update RestrictionForm tasks
 */
async function updateRestrictionForm(data: RegionFormTableRow): Promise<RegionFormTableRow> {
  // Do API call
  const res = await apiClient.post('/regions', {
    id: data.id,
    name: data.name,
    restrictionFormName: data.formName,
    restrictionFormActive: data.isActive,
    restrictionFormDescription: data.description,
  });
  // Toast when finished
  if (res) {
    store.dispatch(
      createToast({
        type: 'success',
        message: 'Updated restriction form successfully',
        timeout: TOAST_TIMEOUT,
      }),
    );
  }
  // Return
  return res.data;
}

/**
 * Fetch all tasks
 */
async function fetchTaskGroups(): Promise<TaskGroupDTO[]> {
  const { data } = await apiClient.get('/taskGroups');
  return data;
}

/**
 * Delete a task group by id
 */
async function deleteTaskGroup(id: number): Promise<void> {
  await apiClient.delete(`/taskGroups/${id}`);
  store.dispatch(
    createToast({
      type: 'success',
      message: 'Deleted task group successfully',
      timeout: TOAST_TIMEOUT,
    }),
  );
}

/**
 * Create or update task (create if id not provided)
 */
async function addOrUpdateTaskGroup(data: TaskGroupDTOPayload): Promise<TaskGroupDTO | null> {
  const res = await apiClient.post('/taskGroups', data);
  if (!!res && res.data) {
    store.dispatch(
      createToast({
        type: 'success',
        message: `${data.id ? 'Updated' : 'Created'} task group successfully`,
        timeout: TOAST_TIMEOUT,
      }),
    );
    return res.data as TaskGroupDTO;
  }
  return null;
}

/**
 * For a region, fetch all taskGroups and tasks. Tasks part of a group will be nested under their respective task.
 */
async function fetchRegionGroupsAndTasksByRegionId(regionId: number): Promise<RegionTasksAndGroupsDTO> {
  const res = await apiClient.get('/regionTaskGroups/regions/' + regionId);
  return res.data;
}

// **** Export **** //

export default {
  fetchTasks,
  fetchMeasureUnits,
  fetchRegions,
  createOrUpdateTask,
  fetchRegionTasksByRegionId,
  updateRegionTasks,
  updateRestrictionForm,
  fetchTaskGroups,
  addOrUpdateTaskGroup,
  deleteTaskGroup,
  fetchRegionGroupsAndTasksByRegionId,
} as const;
